import { useCallback } from 'react';
import { DataTestIdProp } from '../components/common/internal/test-id.types';

export type GetTestId = (...childNames: string[]) => DataTestIdProp;

/**
 * Hook which returns a function to generate a `data-testid` attribute by combining a given `data-testid` attribute (based on the component name by default) and child nodes names (optional).
 * Useful for testing.
 * If no child name is provided to `getTestId`, it will return the `TestIdProp` as is.
 *
 * In the following examples, we showcase the internal implementation of this hook which includes a default value for the `data-testid` attribute (based on the component name), and how you may override it by providing a custom `data-testid` attribute.
 * Note that by overriding the default `data-testid` attribute, you will only change the prefix and not its child nodes names.
 *
 * Example of basic usage:
 * ```tsx
 * // MyButton.tsx
 * const MyButton = ({ 'data-testid': dataTestId = 'my-button', ...props }) => {
 *   const getTestId = useTestId(dataTestId);
 *   return (
 *     <button {...props} {...getTestId()} />
 *   );
 * };
 *
 * // MyButton.spec.tsx
 * const { getByTestId } = render(<MyButton />);
 * expect(getByTestId('my-button')).toBeInTheDocument();
 * // or
 * const { getByTestId } = render(<MyButton data-testid="my-custom-id" />);
 * expect(getByTestId('my-custom-id')).toBeInTheDocument();
 *  *
 * Example of usage with child nodes:
 *
 * // MyButton.tsx
 * const MyButton = ({ 'data-testid': dataTestId = 'my-button', icon, ...props }) => {
 *   const getTestId = useTestId(dataTestId);
 *   return (
 *     <button {...props} {...getTestId()}>
 *       <span {...getTestId('label')}>
 *        <i {...getTestId('label', 'icon')}>{icon}</i>
 *        Click me
 *       </span>
 *     </button>
 *   );
 * };
 *
 * // MyButton.spec.tsx
 * const { getByTestId } = render(<MyButton />);
 * expect(getByTestId('my-button')).toBeInTheDocument();
 * expect(getByTestId('my-button-label')).toHaveTextContent('Click me');
 * expect(getByTestId('my-button-label-icon')).toBeInTheDocument();
 * // or
 * const { getByTestId } = render(<MyButton data-testid="my-custom-id" />);
 * expect(getByTestId('my-custom-id')).toBeInTheDocument();
 * expect(getByTestId('my-custom-id-label')).toHaveTextContent('Click me');
 * expect(getByTestId('my-custom-id-label-icon')).toBeInTheDocument();
 *  */
export const useTestId = (componentName: string): GetTestId => {
    /**
     * Returns a data-testid attribute by combining the component name and child nodes names (optional).
     * If no child name is provided, the data-testid attribute will only contain the component name (provided by useTestId hook).
     *
     * Examples:
     *
     * getTestId()
     * // => { 'data-testid': 'component-name' }
     * getTestId('child-name')
     * // => { 'data-testid': 'component-name-child-name' }
     * getTestId('child-name', 'label')
     * // => { 'data-testid': 'component-name-child-name-label' }
     *    */
    const getTestId = useCallback(
        (...childNames: string[]) => {
            const dataTestId = [componentName, ...childNames]
                .filter(Boolean)
                .join('-');

            return { 'data-testid': dataTestId };
        },
        [componentName]
    );

    return getTestId;
};
